import { logger } from '../logger.ts'
import { ErrorCodes } from './types.ts'
import type { ApiResult } from './types.ts'

/**
 * API client that calls server-side BFF routes (/api/*) instead of Supabase directly.
 * All Supabase credentials and communication are handled server-side.
 * Auth state is managed via httpOnly cookies (set by the server).
 */

class ApiError extends Error {
  code?: string
  statusCode: number

  constructor(statusCode: number, data: { error?: { message?: string; code?: string } }) {
    super(data?.error?.message || 'Unknown error')
    this.code = data?.error?.code
    this.statusCode = statusCode
  }
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`/api${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    ...options,
  })

  const data = await res.json()

  if (!res.ok) {
    throw new ApiError(res.status, data)
  }

  return data as T
}

// Auth types
export interface SessionResponse {
  user: {
    id: string
    email: string
    email_confirmed_at: string | null
    user_metadata: Record<string, unknown>
  } | null
  userRole: string
  emailVerified?: boolean
  phoneVerified?: boolean
}

export interface SignUpResponse {
  user: {
    id: string
    email: string
    email_confirmed_at: string | null
    user_metadata: Record<string, unknown>
  } | null
  needsEmailVerification: boolean
}

// ─── QSK → German Method Migration ───

export interface MigrateFromQskData {
  email: string
  user_id: string
  user_name: string
  gm_start_lesson: number | null
  gm_sequence: Array<number> | null
  was_existing: boolean
  email_sent: boolean
  email_warning: string | null
}

/**
 * Error codes the migration flow can surface to the UI.
 *
 * The `MIGRATION_*` codes originate from the auth_migrate_from_qsk edge
 * function (passed through verbatim by the BFF). `NETWORK_ERROR` and
 * `UNEXPECTED_RESPONSE` are emitted by the BFF or this client when the
 * backend never produced a structured response we could trust.
 */
export type MigrateFromQskErrorCode =
  | 'MIGRATION_USER_NOT_FOUND'
  | 'MIGRATION_ALREADY_COMPLETE'
  | 'MIGRATION_AUTH_CREATE_FAILED'
  | 'MIGRATION_CLIENT_CREATE_FAILED'
  | 'MIGRATION_PROGRESS_COPY_FAILED'
  | 'MIGRATION_SUBSCRIPTION_FAILED'
  | 'MIGRATION_PASSWORD_EMAIL_FAILED'
  | 'MIGRATION_INTERNAL_ERROR'
  | 'NETWORK_ERROR'
  | 'UNEXPECTED_RESPONSE'
  // Future-proof: surface unknown server codes without losing them.
  | (string & {})

export type MigrateFromQskResult =
  | { success: true; data: MigrateFromQskData }
  | {
      success: false
      errorCode: MigrateFromQskErrorCode
      messageDe: string
      messageEn: string
    }

// Frontend-side fallbacks for situations where the backend produced no
// structured message (transport failure, malformed response). The backend's
// own `message_de` / `message_en` are used directly for documented codes.
const FALLBACK_NETWORK_DE =
  'Wir konnten den Server nicht erreichen. Bitte überprüfe deine Internetverbindung und versuche es erneut.'
const FALLBACK_NETWORK_EN =
  'We could not reach the server. Please check your internet connection and try again.'

const FALLBACK_UNEXPECTED_DE =
  'Bei der Migration ist ein unerwarteter Fehler aufgetreten. Bitte versuche es in ein paar Minuten erneut. Wenn das Problem bleibt, kontaktiere den Support.'
const FALLBACK_UNEXPECTED_EN =
  'An unexpected error occurred during migration. Please try again in a few minutes. If the problem persists, contact support.'

function isMigrateSuccessResponse(
  value: unknown,
): value is { success: true; data: MigrateFromQskData } {
  if (!value || typeof value !== 'object') return false
  const v = value as Record<string, unknown>
  if (v.success !== true) return false
  const data = v.data
  if (!data || typeof data !== 'object') return false
  return typeof (data as Record<string, unknown>).email === 'string'
}

function isMigrateFailureResponse(value: unknown): value is {
  success: false
  errorCode: string
  messageDe: string
  messageEn: string
} {
  if (!value || typeof value !== 'object') return false
  const v = value as Record<string, unknown>
  return (
    v.success === false &&
    typeof v.errorCode === 'string' &&
    typeof v.messageDe === 'string' &&
    typeof v.messageEn === 'string'
  )
}

/**
 * Base API client for making calls to BFF API routes.
 * Provides consistent error handling and logging.
 * Maintains backward compatibility with the old ApiClient interface.
 */
export class ApiClient {
  // ─── Auth Methods ───

  static async signIn(email: string, password: string): Promise<SessionResponse> {
    return request<SessionResponse>('/auth/sign-in', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    })
  }

  static async signUp(data: {
    email: string
    password: string
    first_name?: string
    last_name?: string
    gender?: string
    birth_month?: number
    birth_year?: number
    phone_number?: string
  }): Promise<SignUpResponse> {
    return request<SignUpResponse>('/auth/sign-up', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  static async signOut(): Promise<void> {
    await request('/auth/sign-out', { method: 'POST' })
  }

  static async getSession(): Promise<SessionResponse> {
    return request<SessionResponse>('/auth/session')
  }

  static async getUser(): Promise<{ user: SessionResponse['user'] }> {
    return request<{ user: SessionResponse['user'] }>('/auth/get-user')
  }

  static async forgotPassword(email: string): Promise<{ success: boolean; data?: unknown }> {
    return request('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    })
  }

  static async migrateFromQsk(userToken: string): Promise<MigrateFromQskResult> {
    try {
      const response = await request<unknown>('/auth/migrate', {
        method: 'POST',
        body: JSON.stringify({ user_token: userToken }),
      })

      if (isMigrateSuccessResponse(response)) {
        return { success: true, data: response.data }
      }

      if (isMigrateFailureResponse(response)) {
        return {
          success: false,
          errorCode: response.errorCode,
          messageDe: response.messageDe,
          messageEn: response.messageEn,
        }
      }

      // Shouldn't happen — the BFF always emits one of the two shapes — but if
      // it ever does, treat it as an unexpected response rather than crashing.
      return {
        success: false,
        errorCode: 'UNEXPECTED_RESPONSE',
        messageDe: FALLBACK_UNEXPECTED_DE,
        messageEn: FALLBACK_UNEXPECTED_EN,
      }
    } catch (err) {
      // Caught here: BFF unreachable, BFF returned non-2xx (the new contract
      // never does, but pre-deploy or infra failures might), or the response
      // wasn't valid JSON. None of these should surface as a raw error string.
      logger.error(
        'migrateFromQsk transport error',
        err instanceof Error ? err : new Error(String(err)),
        {
          component: 'ApiClient',
          action: 'migrateFromQsk',
        },
      )
      return {
        success: false,
        errorCode: 'NETWORK_ERROR',
        messageDe: FALLBACK_NETWORK_DE,
        messageEn: FALLBACK_NETWORK_EN,
      }
    }
  }

  static async verifyOtp(email: string, token: string, type?: string): Promise<{ success: boolean }> {
    return request('/auth/verify-otp', {
      method: 'POST',
      body: JSON.stringify({ email, token, type }),
    })
  }

  static async resetPassword(password: string): Promise<{ success: boolean }> {
    return request('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ password }),
    })
  }

  static async changePassword(currentPassword: string, newPassword: string): Promise<{ success: boolean }> {
    return request('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    })
  }

  static async resendVerification(email: string): Promise<{ success: boolean }> {
    return request('/auth/resend-verification', {
      method: 'POST',
      body: JSON.stringify({ email }),
    })
  }

  // ─── Edge Function Proxy ───

  /**
   * Invoke a server-side edge function via the BFF proxy.
   * Maintains backward compatibility with the old ApiClient.invoke() interface.
   */
  static async invoke<TResponse = unknown>(
    functionName: string,
    options?: {
      body?: unknown
      skipAuth?: boolean
    },
  ): Promise<ApiResult<TResponse>> {
    try {
      logger.debug(`Invoking ${functionName}`, {
        component: 'ApiClient',
        action: 'invoke',
        metadata: { functionName, body: options?.body },
      })

      const data = await request<{ success: boolean; data: TResponse }>(`/functions/${functionName}`, {
        method: 'POST',
        body: JSON.stringify(options?.body || {}),
      })

      return {
        success: true,
        data: data.data,
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error occurred'
      const code = err instanceof ApiError ? err.code : ErrorCodes.UNKNOWN_ERROR.code

      logger.error(`${functionName} API error`, err instanceof Error ? err : new Error(message), {
        component: 'ApiClient',
        action: 'invoke',
        metadata: { functionName },
      })

      return {
        success: false,
        error: { message, code },
      }
    }
  }
}
